import React from "react";
import { MotionConfig } from "framer-motion";
import { Reveal, SectionHeading, FloatingDecor, Breadcrumb, Glows, PageMeta } from "./ThtParts";
import "./ThtTop.css";
import "./ThtDesign.css";
import "./ThtBusiness.css";
import "./ThtPrivacy.css";
import "./ThtNatural.css";

// 内容は既存のプライバシーポリシー(src/shop/Privacypolicy.tsx)と同じ
const purposes = [
  "入会の手続き、その他契約事務の遂行のため",
  "レッスンやイベント、その他当教室のサービスに関するお問い合わせや、資料のご請求にお応えするため",
  "運営に関するご案内、各種キャンペーン・イベント、当社又は第三者の商品・サービス等のご案内のため",
  "お取引等に関するご連絡のため",
  "お問い合わせ時のご本人確認ならびに内容確認のため",
  "レッスン時にお客様の安全管理を行うため",
  "当教室が利用する傷害保険サービス適用のため",
  "採用選考のため",
  "他社との業務提携のため",
  "アンケート依頼などを含めたマーケティング活動全般のため",
  "顧客動向分析または商品開発等の調査分析のため",
  "個人を特定できない形での統計データの作成、活用、公表のため",
  "その他お客様に通知した利用目的のため",
];

const articles: { title: string; body: React.ReactNode }[] = [
  { title: "個人情報とは", body: "「個人情報」とは、個人情報保護法にいう「個人情報」を指すものとします。" },
  {
    title: "個人情報の収集・利用目的",
    body: (
      <>
        <p>
          個人情報は適切な方法で収集するものとし、情報主体（以下「本人」という）への通知または公表した目的および範囲に限定して利用することとします。
          当教室が個人情報を本人から直接収集し、利用する目的は下記のとおりです。
          なお、当教室と本人の各種契約終了後も、取得した個人情報を利用目的の範囲で利用する場合がございます。
        </p>
        <ol className="pp-purposes">
          {purposes.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ol>
        <p>上記以外の目的で個人情報を収集して利用する際には、その利用目的とお問い合わせ先を明示します。</p>
      </>
    ),
  },
  {
    title: "第三者への情報提供",
    body: "あらかじめ本人より同意を得ない限り、法令に定める場合を除いて、第三者に個人情報を提供することはありません。",
  },
  {
    title: "個人情報の正確性および安全性の確保",
    body: "個人情報の正確性および安全性を確保するため、個人情報に対する安全対策を実施し、個人情報への不正アクセスまたは個人情報の紛失、破壊、改ざん、および漏えい等の予防ならびに是正に努めます。",
  },
  {
    title: "業務委託の見直し・改善",
    body: "業務委託について、個人情報の保護をより確実にするため、見直し・改善を図ります。業務委託契約を締結する際には、委託先に個人情報保護方針の遵守を義務付け、委託先に対する管理・監督を徹底いたします。",
  },
  {
    title: "個人情報保護管理体制及び全従業者への周知・徹底",
    body: "個人情報管理責任者を設置するとともに、その役割を明確にし、適切に個人情報保護に関する活動を行える体制を整備し、さらに継続的な改善を実施いたします。また、全従業者（役員、社員、関係スタッフ）に対して、個人情報保護に関する取組みの周知・徹底を図ります。",
  },
  {
    title: "個人情報の開示、訂正、利用停止、第三者提供の停止",
    body: "ご本人に関する情報の開示、訂正、利用停止および第三者提供の停止の求めがあった場合は、請求者がご本人であることを確認させていただいた上で、特別な理由がない限り、合理的な範囲、期間で対応させていただきます。",
  },
  {
    title: "法令等の遵守",
    body: "個人情報の取扱いにおいて、当該個人情報の保護に適用される法令及び関連するガイドライン・その他の規範を遵守します。",
  },
  {
    title: "お問い合わせ窓口",
    body: (
      <p>
        『個人情報に関するお問い合わせ』は
        <a href="mailto:donichidake-taisou@tht-group.co.jp">donichidake-taisou@tht-group.co.jp</a>
        からお問い合わせください。
      </p>
    ),
  },
];

// パンくずリスト:お問い合わせフォームから開く人が多いので、お問い合わせへ戻れるようにする
const BackButton = () => (
  <Breadcrumb items={[{ label: "お問い合わせ", to: "/contact#form" }, { label: "プライバシーポリシー" }]} />
);

const meta = {
  title: "プライバシーポリシー｜THT合同会社",
  description: "THT合同会社の個人情報の取り扱い(プライバシーポリシー)についてご案内します。",
  canonical: "https://tht-group.co.jp/privacypolicy",
};

const ThtPrivacy = () => (
  <>
    <PageMeta {...meta} />
    <MotionConfig reducedMotion="user">
      <div className="tht-home biz pp">
        <section className="biz-hero">
          <FloatingDecor />
          <div className="content-container">
            <BackButton />
            <SectionHeading en="Privacy Policy" ja="プライバシーポリシー" />
            <Reveal delay={0.1}>
              <p className="lead-text">
                THT合同会社は、お客様の個人情報を適切に取り扱い、保護することを大切にしています。
              </p>
            </Reveal>
          </div>
        </section>

        <section className="biz-section pp-body">
          <FloatingDecor />
          <Glows />
          <div className="content-container">
            {/* 条文は順番に意味があるので番号を付ける */}
            <ol className="pp-articles">
              {articles.map((a, i) => (
                <Reveal key={a.title}>
                  <li className="pp-article">
                    <h2>
                      <span className="pp-num">{String(i + 1).padStart(2, "0")}</span>
                      {a.title}
                    </h2>
                    <div className="pp-text">{typeof a.body === "string" ? <p>{a.body}</p> : a.body}</div>
                  </li>
                </Reveal>
              ))}
            </ol>
            <Reveal className="pp-sign">
              <p>2024年12月1日 制定</p>
              <p>THT合同会社　代表社員　高田知己</p>
            </Reveal>
            <Reveal className="pp-back-bottom">
              <BackButton />
            </Reveal>
          </div>
        </section>
      </div>
    </MotionConfig>
  </>
);

export default ThtPrivacy;
